import { useDroppable } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { Draggable } from "@hello-pangea/dnd";
import clsx from "clsx";
import { GripVertical } from "lucide-react";
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { DoclistTreeItem, DoclistTreeItemSectionsItem } from "~/api/model";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Checkbox } from "~/components/ui/checkbox";
import { TruncatedText } from "~/components/ui/truncated-text";
import { AnnotationIndicator } from "~/features/pageAnnotations/components/AnnotationIndicator";
import { ANNOTATION_COLOR_LIST } from "~/features/pageAnnotations/types";
import { trackEvent } from "~/utils/analytics";
import { CategoryItem } from "../../types";
import { fuzzyHighlight } from "../../utils/fuzzyFilter";
import { DocListPage } from "./DocListPage";
import { DocListSectionInput } from "./DocListSectionInput";
import { NewSectionButton } from "./NewSectionButton";
import { SectionActions } from "./SectionActions";

interface SectionProps {
  organizationId: string;
  projectId: string;
  chatId: string;
  onDoclistOutdated: () => void;
  section: DoclistTreeItemSectionsItem;
  index: number;
  isExportMode: boolean;
  isViewOnly: boolean;
  isSelected: boolean;
  query: string;
  onCheckboxChange: (sectionId: string, checked: boolean) => void;
  onClick: (
    section: DoclistTreeItemSectionsItem,
    event?: React.MouseEvent
  ) => void;
  categories: CategoryItem[];
  onConfirmMoveTo: (section: DoclistTreeItemSectionsItem) => void;
  isProcessing: boolean;
  onSplit: (section: DoclistTreeItemSectionsItem) => void;
  items: DoclistTreeItem[];
  onMerge: (section: DoclistTreeItemSectionsItem) => void;
  selectedPageId: string | null;
  onPageSelect: (pageId: string) => void;
  absolutePageNumbers: Record<string, number>;
  openSections: string[];
  className?: string;
  multiSelectedPageIds?: string[];
  onMultiSelect?: (pageId: string, modifier: "none" | "shift" | "cmd") => void;
  onDeleteSection: (section: DoclistTreeItemSectionsItem) => void;
  onAutoRename: (section: DoclistTreeItemSectionsItem) => void;
  onRotate: (section: DoclistTreeItemSectionsItem) => void;
  isAiRenaming?: boolean;
  isSelectedForMerge?: boolean;
  isMultiSelectMode?: boolean;
  isActive: boolean;
  isCurrentSection: boolean;
  onToggleExpand: (sectionId: string) => void;
  /** Map of pageId to annotation colors for pages in this section */
  pageAnnotationColors?: Map<string, string[]>;
}

const DocListCategorySectionComponent: FC<SectionProps> = ({
  organizationId,
  projectId,
  chatId,
  onDoclistOutdated,
  section,
  index,
  isExportMode,
  isViewOnly,
  isSelected,
  query,
  onCheckboxChange,
  onClick,
  categories,
  onConfirmMoveTo,
  isProcessing,
  onSplit,
  items,
  onMerge,
  selectedPageId,
  onPageSelect,
  absolutePageNumbers,
  openSections,
  className,
  multiSelectedPageIds = [],
  onMultiSelect,
  onDeleteSection,
  onAutoRename,
  onRotate,
  isAiRenaming = false,
  isSelectedForMerge = false,
  isMultiSelectMode = false,
  isActive,
  isCurrentSection,
  onToggleExpand,
  pageAnnotationColors,
}) => {
  const [renamedSectionId, setRenamedSectionId] = useState<string | null>(null);
  const [editedSectionId, setEditedSectionId] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const highlightedSectionName = useMemo(() => {
    if (!query) return null;
    return fuzzyHighlight(section.name, query);
  }, [query, section.name]);

  const sectionAnnotationColors = useMemo(() => {
    if (!pageAnnotationColors) return [];
    const colorSet = new Set<string>();
    for (const pid of section.pageIds ?? []) {
      const pageColors = pageAnnotationColors.get(pid);
      if (pageColors) {
        for (const c of pageColors) colorSet.add(c);
      }
    }
    const colors = Array.from(colorSet);
    const colorOrder: string[] = ANNOTATION_COLOR_LIST;
    colors.sort((a, b) => colorOrder.indexOf(b) - colorOrder.indexOf(a));
    return colors;
  }, [section.pageIds, pageAnnotationColors]);

  const pageMetaById = useMemo(() => {
    const map = new Map<
      string,
      {
        previewUrl: string | null;
        rotate: number;
      }
    >();

    section.pages?.forEach((page) => {
      map.set(page.id, {
        previewUrl: page.previewUrl ?? null,
        rotate: page.rotate ?? 0,
      });
    });

    return map;
  }, [section.pages]);

  const getTextClass = () => {
    if (isProcessing) return "text-muted-foreground";

    if (isExportMode) {
      return isSelected || isCurrentSection
        ? "text-foreground"
        : "text-muted-foreground";
    } else {
      return "text-foreground";
    }
  };

  const isExpanded = openSections.includes(section.id);

  const handleRenameClick = (sectionId: string) => {
    setRenamedSectionId(sectionId);
  };

  const handleCancelRename = () => {
    setRenamedSectionId(null);
  };

  //dnd-kit for page
  const { setNodeRef } = useDroppable({
    id: `section-${section.id}`,
    data: {
      type: "section",
      sectionId: section.id,
      pageIds: section.pageIds,
    },
  });

  const { setNodeRef: setNewSectionNodeRef, isOver: isNewSectionOver } =
    useDroppable({
      id: `new-section-${section.id}`,
      data: {
        type: "new-section",
        sectionId: section.id,
      },
      disabled: isExportMode || isViewOnly,
    });

  useEffect(() => {
    setIsDraggingOver(isNewSectionOver);
  }, [isNewSectionOver]);

  const handleMoveTo = useCallback(
    () => onConfirmMoveTo(section),
    [onConfirmMoveTo, section]
  );
  const handleSplitSection = useCallback(
    () => onSplit(section),
    [onSplit, section]
  );
  const handleMergeSection = useCallback(
    () => onMerge(section),
    [onMerge, section]
  );
  const handleDeleteSection = useCallback(
    () => onDeleteSection(section),
    [onDeleteSection, section]
  );
  const handleAutoRename = useCallback(
    () => onAutoRename(section),
    [onAutoRename, section]
  );
  const handleRotateSection = useCallback(
    () => onRotate(section),
    [onRotate, section]
  );

  const renderPages = () => {
    if (!isExpanded) {
      return null;
    }

    const pageIds = section.pageIds || [];
    return (
      <div ref={setNodeRef}>
        <SortableContext
          key={pageIds.join(",")}
          items={pageIds}
          strategy={rectSortingStrategy}
        >
          <div className="relative grid grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-2 rounded-md px-[2px]">
            {pageIds.map((pageId, index) => (
              <DocListPage
                key={pageId}
                pageId={pageId}
                pageNumber={index + 1}
                pageImg={pageMetaById.get(pageId)?.previewUrl ?? null}
                sectionId={section.id}
                isDraggable={!isExportMode && !isViewOnly}
                index={index}
                isSelected={selectedPageId === pageId}
                isMultiSelected={multiSelectedPageIds.includes(pageId)}
                onSelect={onPageSelect}
                onMultiSelect={onMultiSelect}
                absolutePageNumbers={absolutePageNumbers}
                rotation={pageMetaById.get(pageId)?.rotate ?? 0}
                annotationColors={pageAnnotationColors?.get(pageId)}
              />
            ))}
            {(!section.pageIds || section.pageIds.length === 0) && (
              <div className="col-span-full h-[2px]"></div>
            )}
          </div>
        </SortableContext>
      </div>
    );
  };

  const renderSectionActions = () => (
    <>
      <SectionActions
        section={section}
        className={clsx(
          "flex-shrink-0 transition-opacity duration-300",
          editedSectionId === section.id
            ? "opacity-100"
            : "opacity-0 group-hover:opacity-100"
        )}
        handleOpenChange={(isOpen) => {
          setEditedSectionId(isOpen ? section.id : null);
        }}
        handleRename={() => handleRenameClick(section.id)}
        onAutoRename={handleAutoRename}
        isAiRenaming={isAiRenaming}
        categories={categories.filter((c) => c.id !== section.categoryId)}
        onMoveTo={handleMoveTo}
        onSplit={handleSplitSection}
        items={items}
        onMerge={handleMergeSection}
        onDelete={handleDeleteSection}
        onRotate={handleRotateSection}
        isMultiSelectMode={isMultiSelectMode}
        isSelectedForMerge={isSelectedForMerge}
      />
    </>
  );

  return (
    <Draggable
      key={section.id}
      draggableId={`section-${section.id}`}
      index={index}
      isDragDisabled={isExportMode || isViewOnly || isMultiSelectMode}
    >
      {(provided) => (
        <>
          {!isViewOnly && (
            <NewSectionButton
              isDraggingOver={isDraggingOver}
              setNewSectionNodeRef={setNewSectionNodeRef}
              sectionId={section.id}
            />
          )}
          <AccordionItem
            value={section.id}
            className={clsx("border-none", className)}
            ref={provided.innerRef}
            {...provided.draggableProps}
            onClick={(e) => onClick(section, e)}
            data-section-id={section.id}
          >
            {renamedSectionId === section.id ? (
              <DocListSectionInput
                section={section}
                organizationId={organizationId}
                projectId={projectId}
                chatId={chatId}
                onDoclistOutdated={onDoclistOutdated}
                onCancel={handleCancelRename}
              />
            ) : (
              <AccordionTrigger
                expandOnlyByChevron={true}
                isChevronLeft={true}
                disabled={isMultiSelectMode}
                hideChevron={isMultiSelectMode}
                onDoubleClick={() => {
                  if (!isExportMode && !isViewOnly && !isMultiSelectMode) {
                    onToggleExpand(section.id);
                    trackEvent({
                      eventName:
                        "app/doc_list/section_double_click_toggle.section/dblclick",
                    });
                  }
                }}
                className={clsx(
                  "bg-background text-ellipsis truncate gap-[2px]",
                  editedSectionId === section.id
                    ? "bg-blue-50 dark:bg-blue-950"
                    : isCurrentSection
                      ? "hover:none"
                      : "hover:bg-blue-50 dark:hover:bg-blue-950",
                  "rounded-md cursor-pointer group gap-[2px]",
                  isExportMode ? "pr-3" : "px-3 py-2",
                  isActive ? "shadow-[inset_0_0_0_1px_#60A5FA]" : "",
                  isCurrentSection && "bg-blue-100 dark:bg-blue-900",
                  isSelectedForMerge && "!bg-blue-200 dark:!bg-blue-800",
                  className
                )}
              >
                <div
                  className={`flex items-center w-full max-w-[calc(100%-24px)]`}
                >
                  {!isViewOnly &&
                    !isExportMode &&
                    !isMultiSelectMode &&
                    !openSections.includes(section.id) && (
                      <div
                        {...provided.dragHandleProps}
                        className="hidden group-hover:block"
                      >
                        <GripVertical className="w-4 h-4 text-muted-foreground mr-2 -ml-2" />
                      </div>
                    )}
                  {isExportMode && (
                    <label
                      className="flex items-center pl-3 pr-2 py-[10px] cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          trackEvent({
                            eventName: checked
                              ? "app/doc_list/select_category.checkbox/click"
                              : "app/doc_list/deselect_category.checkbox/click",
                          });
                          onCheckboxChange(section.id, checked === true);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isProcessing}
                        className="border-gray-300 cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border shadow-none"
                      />
                    </label>
                  )}
                  <div className="flex items-center w-full min-w-0 text-ellipsis truncate overflow-hidden">
                    <div
                      className={`text-sm ${getTextClass()} font-normal font-inter leading-tight truncate flex-1 min-w-0 flex gap-2 overflow-hidden`}
                    >
                      {query ? (
                        <div
                          className={clsx(
                            "flex-1 min-w-0 max-w-full truncate",
                            isAiRenaming && "animate-pulse-fade"
                          )}
                        >
                          {highlightedSectionName}
                        </div>
                      ) : (
                        <TruncatedText
                          text={section.name}
                          className={clsx(
                            "flex-1 min-w-0 max-w-full",
                            isAiRenaming && "animate-pulse-fade"
                          )}
                        />
                      )}
                      {!isViewOnly && !isExportMode && renderSectionActions()}
                    </div>
                    <div
                      className={clsx(
                        "text-sm ml-2 flex-shrink-0 min-w-4 text-right",
                        isCurrentSection
                          ? "text-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      {section.pageCount}
                    </div>
                  </div>
                  {sectionAnnotationColors.length > 0 && (
                    <AnnotationIndicator
                      colors={sectionAnnotationColors}
                      className="top-1 right-2"
                      dotSize={6}
                      gap={-2}
                    />
                  )}
                </div>
              </AccordionTrigger>
            )}
            <AccordionContent className="pb-2 pt-1 pl-2">
              {renderPages()}
            </AccordionContent>
          </AccordionItem>
        </>
      )}
    </Draggable>
  );
};

export const DocListCategorySection = memo(DocListCategorySectionComponent);
